import { useNavigate } from "@tanstack/react-router"
import { createColumnHelper, flexRender, Header, useTable } from "@tanstack/react-table"
import { Badge } from "design-system/atoms/Badge"
import { Button } from "design-system/atoms/Button"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "design-system/atoms/Tooltip"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { cn } from "design-system/utils"
import { features, type Features } from "lib/table"
import { History, MicOff } from "lucide-react"
import { useGoalsActivityQuery } from "modules/campaigns/hooks/sections/goals/useGoalsActivityQuery"
import { useGoalsQuery } from "modules/campaigns/hooks/sections/goals/useGoalsQuery"
import { ActivityMiniature } from "modules/insights/components/Activity/ActivityMiniature"
import { ChangeLogItem } from "modules/shared/components/ChangeLogItem"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Route as GoalsManagementRoute } from "routes/_base/_auth/campaigns/goals/list/route"
import { ChangeLogModel, GoalsResponseDataSchema, WeeklyActivityResponseDataSchema } from "schema"
import { z } from "zod"
import { GoalsSortFields } from "."

const columnHelper = createColumnHelper<Features, z.infer<typeof GoalsResponseDataSchema>[number]>()

type GoalsQueryReturn = ReturnType<typeof useGoalsQuery>

type Props = {
  goals: GoalsQueryReturn["data"]
  isPending: GoalsQueryReturn["isPending"]
  fields: GoalsSortFields[]
  onSort: (field: GoalsSortFields) => void
  search: ReturnType<typeof GoalsManagementRoute.useSearch>
}

export const GoalsTable = ({ goals, isPending, fields, onSort, search }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [changeLogGoalId, setChangeLogGoalId] = useState<string | null>(null)

  const goalIds = useMemo(() => goals?.map((goal) => goal.id), [goals])

  const { data: goalsData, isPending: isGoalsActivityPending } = useGoalsActivityQuery({
    params: { goalIds: goalIds ?? [] },
    enabled: (goalIds?.length ?? 0) > 0,
  })

  const goalsMap = useMemo(() => {
    return goalsData?.weeklyCounts.reduce((acc, goal) => {
      acc.set(goal.entityId, goal)
      return acc
    }, new Map<string, z.infer<typeof WeeklyActivityResponseDataSchema>["weeklyCounts"][number]>())
  }, [goalsData])

  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor("name", {
          header: t("campaigns.goalsManagement.table.name"),
          cell: (props) => {
            const name = props.row.original.displayName
            const isUnderlying = props.row.original.isUnderlying

            return (
              <TooltipProvider>
                <div className="flex flex-row items-center justify-start gap-4">
                  <div className="w-4 relative flex items-center justify-center">
                    {isUnderlying && (
                      <Tooltip>
                        <TooltipTrigger>
                          <MicOff className="size-4" />
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          {t("campaigns.goals.underlyingDescription")}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  <div className="max-w-full overflow-hidden flex-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className={cn("truncate block", !name && "text-muted-foreground")}>
                          {name ?? t("campaigns.untitled")}
                        </span>
                      </TooltipTrigger>
                      {name && (
                        <TooltipContent>
                          <p className="max-w-xs break-words">{name}</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </div>
                </div>
              </TooltipProvider>
            )
          },
          meta: { className: "w-2/8" },
        }),
        columnHelper.accessor("assistantInstructions", {
          header: t("campaigns.goalsManagement.table.description"),
          cell: (props) => {
            const assistantInstructions = props.getValue()
            return (
              <div className="max-w-full overflow-hidden">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        className={cn(
                          "truncate block",
                          !assistantInstructions && "text-muted-foreground",
                        )}
                      >
                        {assistantInstructions}
                      </span>
                    </TooltipTrigger>
                    {assistantInstructions && (
                      <TooltipContent>
                        <p className="max-w-xs break-words">{assistantInstructions}</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              </div>
            )
          },
          meta: { className: "w-2/8" },
        }),
        columnHelper.accessor("type", {
          header: t("campaigns.goalsManagement.table.type"),
          cell: (props) => (
            <Badge variant="secondary" key="trigger-value">
              {props.getValue()}
            </Badge>
          ),
          meta: { className: "w-1/8", align: "center" },
        }),
        columnHelper.accessor("activeCampaigns", {
          header: t("campaigns.goalsManagement.table.activeCampaigns"),
          meta: { className: "w-1/8", align: "right" },
        }),
        columnHelper.accessor("id", {
          header: t("campaigns.goalsManagement.table.activity"),
          cell: (props) => {
            const goalId = props.row.original.id.toString()
            return (
              <ActivityMiniature
                data={goalsMap?.get(goalId)}
                isPending={isGoalsActivityPending}
                className="pl-4"
                color="chart-3"
                areaChart
              />
            )
          },
          meta: { className: "w-1/8", align: "right" },
        }),
        columnHelper.accessor("createdAt", {
          header: t("campaigns.table.createdAt"),
          cell: (createdAt) => <DateDisplay date={createdAt.getValue()} />,
          meta: { className: "w-1/8", align: "right" },
        }),
        columnHelper.display({
          id: "changeLog",
          header: "",
          cell: (props) => {
            const goal = props.row.original
            if (!goal.hasChangeLog) return null
            return (
              <div className="flex justify-end min-w-0">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation()
                    setChangeLogGoalId(goal.id.toString())
                  }}
                >
                  <History className="size-4 shrink-0" />
                </Button>
              </div>
            )
          },
          meta: { className: "w-20 min-w-0 overflow-hidden", align: "right" },
        }),
      ]),
    [t, goalsMap, isGoalsActivityPending],
  )

  const table = useTable({
    data: goals ?? [],
    columns,
    features,
  })

  function renderContent() {
    if (isPending || goals === undefined) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (goals.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("campaigns.goalsManagement.table.empty")}
        />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow
        key={row.original.id}
        className="h-16 cursor-pointer"
        onClick={() =>
          navigate({
            to: "/campaigns/goals/list/edit/$id",
            params: { id: row.original.id },
            search,
          })
        }
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} {...cell.column.columnDef.meta}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  function buildSortable(
    header: Header<Features, z.infer<typeof GoalsResponseDataSchema>[number], unknown>,
  ) {
    if (!fields.includes(header.id as GoalsSortFields)) return

    const isActive = search.field === header.id

    return {
      active: isActive,
      direction: isActive ? search.order : "asc",
      onClick: () => onSort(header.id as GoalsSortFields),
    }
  }

  const changeLogGoal = goals?.find((g) => g.id.toString() === changeLogGoalId)

  return (
    <>
      {changeLogGoalId && (
        <ChangeLogItem
          open={!!changeLogGoalId}
          onOpenChange={(open) => {
            if (!open) setChangeLogGoalId(null)
          }}
          model={ChangeLogModel.Goal}
          recordId={changeLogGoalId}
          title={changeLogGoal?.displayName ?? null}
        />
      )}
      <Table className="table-fixed">
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  sortable={buildSortable(header)}
                  {...header.column.columnDef.meta}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>{renderContent()}</TableBody>
      </Table>
    </>
  )
}
